// Lumia Kids — İsim Tasarımcısı (satır içi sürüm, modal/önizleme yok)
// <name-designer> özel elemanı:
//  - İsim alanı + talla (boyut) + çanta rengi + ip rengi + süs elementleri
//    (Figuras/Flor/Mariposa/Arcoíris), sepete ekle düğmesinin üzerinde
//    tek bölüm olarak durur
//  - properties[Nombre a bordar] / properties[Talla] / properties[Color del hilo] /
//    properties[Elementos] / properties[_config] girdileri form attribute'u ile
//    ürün formuna bağlıdır (form dışında kalma hatası yaşanmaz; FormData
//    form-associated elemanları içerir)
//  - Ücretlendirme knitandcarry modeli: her seçili element KATEGORİSİ sabit
//    ücret ekler; varyantlar "Sin elementos", "1 elemento" ... "4 elementos"
//    adımlarıdır ve Talla (Mediana/Pequeña) ile çarpılır. Kategori içi kart
//    sayısı (2 figür, 2 çiçek) fiyatı DEĞİŞTİRMEZ.
//  - Modül kapsamı dışında değişken tanımlanmaz; dinleyiciler
//    AbortController ile temizlenir, yeniden render'da çoğalmaz.
//  - Paket (bundle) modu: birincil panel + data-nd-secondary ikincil panel.
//    İkincil panel forma bağlanmaz, varyant radio'larına dokunmaz; iki
//    çanta tek JSON items[] isteğiyle sepete eklenir (BxGy indirimi
//    mağaza tarafında otomatik uygulanır).

import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';

// İspanyolca ad karakterleri (áéíóúüñ) + Türkçe karakterler (TR mağazasındaki
// draft önizlemede Türkçe isim denenebilsin diye) izinlidir.
const NAME_DISALLOWED = /[^A-Za-zÁÉÍÓÚÜÑáéíóúüñÇĞİÖŞçğıöş ]/g;
const STORAGE_PREFIX = 'lumia-name-designer:';
// Elementler kategori bazında ücretlendirilir; ücret üründeki adet
// varyantlarıyla alınır: 0 kategori → ELEMENT_NONE_VALUE varyantı,
// n kategori → "n elemento(s)" varyantı.
const ELEMENT_NONE_VALUE = 'Sin elementos';
const elementCountValue = (count) =>
  count > 0 ? count + (count === 1 ? ' elemento' : ' elementos') : ELEMENT_NONE_VALUE;
const TALLA_DEFAULT = 'Mediana';

// Aksanları sadeleştirip küçük harfe indirger; renk etiketini dosya adıyla
// eşleştirmek için kullanılır ("Kiremit" -> "kiremit", "Canela" -> "canela").
const slugifyTr = (value) =>
  String(value || '')
    .toLocaleLowerCase('tr')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

class NameDesigner extends HTMLElement {
  #abort = null;
  #observer = null;
  #patchedComponent = null;
  #originalHandleSubmit = null;
  #refs = {};
  #data = { productId: '', options: [], variants: [] };
  #state = {
    name: '',
    talla: TALLA_DEFAULT,
    yarnLabel: '',
    yarnDisplay: '',
    yarnHex: '',
    bagLabel: '',
    bagVariantValue: '',
    bagGalleryKey: '',
    feltLabels: [],
    feltColors: {},
    extraElements: [],
  };
  // Hangi element kategorilerinin kart şeridi açık (yalnızca arayüz durumu)
  #openCats = new Set();
  #syncingAppField = false;
  #bundle = 1;
  #bundleSubmitting = false;
  #errorTimeout = null;
  #gallerySlug = '';

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#collectRefs();
    this.#readProductData();
    if (!this.#refs.cardInput) return;

    // İkincil panelin swatch kaynağı boş gelir; butonlar birincilden klonlanır.
    if (this.#isSecondary) this.#cloneSwatchesFromPrimary();
    this.#distributeSwatches();
    this.#restoreState();

    this.#refs.cardInput.addEventListener('input', () => {
      this.#setName(this.#refs.cardInput.value, this.#refs.cardInput);
    }, { signal });

    // Aris Product Options uygulamasının isim alanı da sayfada görünür
    // (mağaza tercihi: iki arayüz birlikte). İki yönlü senkron kurulur ki
    // müşteri hangisine yazarsa yazsın diğeri dolsun ve uygulamanın
    // "alan boş" engeli sepete eklemeyi durdurmasın. Formda tek alan
    // olduğundan yalnızca birincil panel bağlanır.
    if (!this.#isSecondary) this.#bindAppField(signal);

    this.querySelectorAll('[data-nd-talla]').forEach((button) => {
      button.addEventListener('click', () => this.#selectTalla(button.dataset.ndTalla || TALLA_DEFAULT), { signal });
    });
    this.querySelectorAll('[data-nd-element]').forEach((button) => {
      button.addEventListener('click', () => this.#toggleElement(button.dataset.ndElement || ''), { signal });
    });
    this.querySelectorAll('[data-nd-swatch="bag"]').forEach((button) => {
      button.addEventListener('click', () => this.#selectBag(button), { signal });
    });
    this.querySelectorAll('[data-nd-swatch="yarn"]').forEach((button) => {
      button.addEventListener('click', () => this.#selectYarn(button), { signal });
    });
    // Figür kartı tek düğme değil: içinde seçim düğmesi ve (varsa)
    // renk noktaları olan bir kap. Tek dinleyici ikisini de karşılar.
    this.querySelectorAll('[data-nd-swatch="felt"]').forEach((card) => {
      card.addEventListener('click', (event) => {
        const dot = event.target instanceof Element ? event.target.closest('[data-nd-felt-color]') : null;
        if (dot) this.#selectFeltColor(card, dot);
        else this.#toggleFelt(card);
      }, { signal });
    });

    if (!this.#isSecondary) {
      this.#guardAddToCart();
      const sectionRoot = this.#section();
      if (sectionRoot) {
        this.#observer = new MutationObserver(() => {
          this.#guardAddToCart();
          this.#bindAppField(this.#abort?.signal);
        });
        this.#observer.observe(sectionRoot, { childList: true, subtree: true });
      }

      this.#bindBundleCards(signal);
      this.#setBundle(this.#bundle);

      // Tema, varyant değişiminde sticky sepet çubuğunun fiyat kabını
      // morph'layarak yeniden yazar; bu bizim boyadığımız değeri ezer.
      // Paket modunda gösterilecek tutar varyant fiyatından farklı
      // olduğundan olay sonrası (tüm eşzamanlı dinleyiciler bittikten
      // sonra) yeniden boyanır.
      this.#section()?.addEventListener(
        ThemeEvents.variantUpdate,
        () => requestAnimationFrame(() => this.#refreshPriceDisplays()),
        { signal }
      );
    }

    this.#syncHiddenInputs();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#patchedComponent && this.#originalHandleSubmit) {
      this.#patchedComponent.handleSubmit = this.#originalHandleSubmit;
      delete this.#patchedComponent.dataset.nameDesignerGuarded;
    }
    this.#patchedComponent = null;
    this.#originalHandleSubmit = null;
  }

  get value() {
    return this.#state.name.trim();
  }

  get bundle() {
    return this.#bundle;
  }

  // Ücrete esas seçili element kategorisi sayısı (0–4).
  get elementCount() {
    return this.#activeElements().length;
  }

  // Panelin mevcut seçimine (Talla + element sayısı) karşılık gelen varyant.
  // Talla seçeneği olmayan üründe yalnızca element adımına düşülür — TR
  // mağazasındaki draft önizlemede hiçbiri eşleşmez, fiyat boyama sessizce
  // devre dışı kalır (beklenen durum, README.md).
  currentVariant() {
    return (
      this.#findVariant([this.#state.talla, elementCountValue(this.elementCount)]) ||
      this.#findVariant([elementCountValue(this.elementCount)])
    );
  }

  // Satır (line item) property'leri — #syncHiddenInputs ile aynı kaynaktan
  // üretilir; bundle ekleme yolunda item başına gönderilir.
  lineProperties() {
    const elementsText = this.#elementsText();
    const config = [
      this.#state.name.trim(),
      this.#state.yarnHex || '',
      this.#state.bagLabel || '',
      this.#state.talla || '',
      elementsText,
    ].join('|');
    return {
      'Nombre a bordar': this.#state.name.trim(),
      'Talla': this.#state.talla || TALLA_DEFAULT,
      'Color de la mochila': this.#state.bagLabel || '',
      'Color del hilo': this.#state.yarnDisplay || this.#state.yarnLabel || '',
      'Elementos': elementsText,
      '_config': config.slice(0, 255),
    };
  }

  // İkincil panel element değiştirdiğinde birincil paneli dürtmek için.
  refreshPrice() {
    if (!this.#isSecondary) this.#refreshPriceDisplays();
  }

  showError() {
    this.#toggleError(this.#refs.cardError, true);
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.#refs.cardInput?.focus({ preventScroll: true });
  }

  appNameValue() {
    return this.#appNameField()?.value || '';
  }

  adoptName(value) {
    this.#setName(value, null);
  }

  // Submit öncesi: isim + seçimleri uygulamanın alanlarına garanti et.
  ensureAppSync() {
    this.#syncAppName();
    this.#syncAppRadio('Çanta Rengi', this.#state.bagLabel);
    this.#syncAppRadio('İplik Rengi', this.#state.yarnLabel);
  }

  // --- kurulum ----------------------------------------------------------

  #collectRefs() {
    this.#refs = {
      cardInput: this.querySelector('[data-nd-card-input]'),
      cardError: this.querySelector('[data-nd-card-error]'),
      cardHintCount: this.querySelector('[data-nd-chars]'),
      tallaProperty: this.querySelector('[data-nd-talla-property]'),
      bagProperty: this.querySelector('[data-nd-bag-property]'),
      yarnProperty: this.querySelector('[data-nd-yarn-property]'),
      elementsProperty: this.querySelector('[data-nd-elements-property]'),
      configProperty: this.querySelector('[data-nd-config-property]'),
      bagList: this.querySelector('[data-nd-bag-list]'),
      yarnList: this.querySelector('[data-nd-yarn-list]'),
      feltList: this.querySelector('[data-nd-felt-list]'),
      feltSummary: this.querySelector('[data-nd-felt-summary]'),
      elementHint: this.querySelector('[data-nd-element-hint]'),
      swatchSource: this.querySelector('[data-nd-swatch-source]'),
      summary: this.querySelector('[data-nd-summary]'),
    };
  }

  #readProductData() {
    // İkincil panel kendi JSON'unu taşımaz; section içindeki (birincil)
    // kopyadan okur.
    const script = this.querySelector('script[data-nd-data]') || this.#section()?.querySelector('script[data-nd-data]');
    if (!script) return;
    try {
      this.#data = JSON.parse(script.textContent);
    } catch (error) {
      // veri bozuksa varyant eşleşmesi devre dışı kalır, seçenekler çalışır
    }
  }

  // İkincil panel: birincil panelin swatch butonlarını klonlar. Tema
  // editörü blok hedefleri tekil kalsın diye editor attribute'ları sıyrılır.
  #cloneSwatchesFromPrimary() {
    const source = this.#refs.swatchSource;
    if (!source || source.children.length > 0) return;
    const primary = this.#primary();
    if (!primary) return;
    primary.querySelectorAll('[data-nd-swatch]').forEach((button) => {
      const clone = button.cloneNode(true);
      // Figür kartlarında aria-pressed iç düğmede/renk noktalarında durur,
      // renk alt blokları da kendi editor attribute'larını taşır.
      for (const el of [clone, ...clone.querySelectorAll('*')]) {
        if (el.hasAttribute('aria-pressed')) el.setAttribute('aria-pressed', 'false');
        for (const attr of Array.from(el.attributes)) {
          if (attr.name.startsWith('data-shopify-editor')) el.removeAttribute(attr.name);
        }
      }
      source.appendChild(clone);
    });
  }

  // Alt bloklar tek content_for akışında gelir; tür bazında listelere taşınır.
  #distributeSwatches() {
    const { swatchSource, bagList, yarnList, feltList } = this.#refs;
    if (!swatchSource) return;
    const targets = { bag: bagList, yarn: yarnList, felt: feltList };
    swatchSource.querySelectorAll('[data-nd-swatch]').forEach((button) => {
      const target = targets[button.dataset.ndSwatch] || yarnList;
      target?.appendChild(button);
    });
    [bagList, yarnList].forEach((list) => {
      if (list && !list.children.length) list.closest('[data-nd-field]')?.setAttribute('hidden', '');
    });
  }

  // İsim sınırı seçili Talla'ya bağlıdır (Mediana 10 / Pequeña 7 —
  // blok ayarlarından gelir).
  get #maxChars() {
    const key = this.#state.talla === 'Pequeña' ? 'maxCharsSmall' : 'maxCharsMedium';
    const parsed = Number.parseInt(this.dataset[key] || this.dataset.maxChars || '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
  }

  get #storageKey() {
    return STORAGE_PREFIX + (this.#data.productId || this.dataset.productId || 'product') + (this.#isSecondary ? ':2' : '');
  }

  get #isSecondary() {
    return this.hasAttribute('data-nd-secondary');
  }

  get #bundleDiscountCents() {
    const parsed = Number.parseInt(this.dataset.bundleDiscountCents || '', 10);
    // Yedek değer bloktaki bundle_discount varsayılanıyla (30,00 €) aynı kalmalı.
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 3000;
  }

  #section() {
    return this.closest('.shopify-section') || null;
  }

  #primary() {
    const el = this.#section()?.querySelector('name-designer:not([data-nd-secondary])');
    return el instanceof NameDesigner ? el : null;
  }

  #secondary() {
    const el = this.#section()?.querySelector('name-designer[data-nd-secondary]');
    return el instanceof NameDesigner ? el : null;
  }

  // --- durum ------------------------------------------------------------

  #restoreState() {
    let stored = null;
    try {
      stored = JSON.parse(window.sessionStorage.getItem(this.#storageKey) || 'null');
    } catch (error) {
      stored = null;
    }
    if (stored && typeof stored === 'object') {
      if (stored.talla === 'Pequeña' || stored.talla === 'Mediana') this.#state.talla = stored.talla;
      this.#state.name = this.#sanitize(stored.name || '');
      if (!this.#isSecondary) this.#bundle = stored.bundle === 2 ? 2 : 1;
    }

    const yarnButtons = Array.from(this.querySelectorAll('[data-nd-swatch="yarn"]'));
    const storedYarn = stored?.yarnLabel
      ? yarnButtons.find((b) => b.dataset.value === stored.yarnLabel)
      : null;
    this.#applyYarn(storedYarn || yarnButtons[0] || null);

    const bagButtons = Array.from(this.querySelectorAll('[data-nd-swatch="bag"]'));
    const storedBag = stored?.bagLabel
      ? bagButtons.find((b) => b.dataset.value === stored.bagLabel)
      : null;
    this.#applyBag(storedBag || bagButtons[0] || null, { switchVariant: false });

    const feltButtons = Array.from(this.querySelectorAll('[data-nd-swatch="felt"]'));
    const storedFelt = Array.isArray(stored?.feltLabels) ? stored.feltLabels : [];
    this.#state.feltLabels = storedFelt.filter((label) =>
      feltButtons.some((b) => b.dataset.value === label)
    );

    // Kartsız kategoriler (Arcoíris ve henüz kartı olmayan Mariposa)
    // yalnızca düğme durumundan ibarettir; bilinmeyenler elenir.
    const knownExtras = Array.from(this.querySelectorAll('[data-nd-element]')).map((b) => b.dataset.ndElement);
    const storedExtras = Array.isArray(stored?.extraElements) ? stored.extraElements : [];
    this.#state.extraElements = storedExtras.filter(
      (cat) => knownExtras.includes(cat) && !this.#categoryHasCards(cat)
    );

    // Renk çeşidi olan figürlerde geçerli bir renk her zaman seçili durur;
    // kayıtlı renk artık yoksa ilk renge düşülür.
    const storedColors = stored?.feltColors && typeof stored.feltColors === 'object' ? stored.feltColors : {};
    this.#state.feltColors = {};
    feltButtons.forEach((card) => {
      const dots = Array.from(card.querySelectorAll('[data-nd-felt-color]'));
      if (!dots.length) return;
      const label = card.dataset.value || '';
      const match = dots.find((dot) => dot.dataset.colorLabel === storedColors[label]);
      this.#state.feltColors[label] = (match || dots[0]).dataset.colorLabel || '';
    });

    // Seçimi olan kategorilerin kart şeridi açık başlar; hiç seçim yoksa
    // ilk kartlı kategori (Figuras) tıklama beklemeden açık gelir.
    this.#openCats = new Set(this.#selectedCardCategories());
    this.#openDefaultCategory();

    this.#applyTalla(this.#state.talla);
    this.#renderElements();
    this.#renderFeltState();
    this.#renderFeltSummary();
    this.#syncVariant();

    if (this.#refs.cardInput && !this.#refs.cardInput.value) {
      this.#refs.cardInput.value = this.#state.name;
    } else if (this.#refs.cardInput) {
      this.#state.name = this.#sanitize(this.#refs.cardInput.value);
    }
  }

  #persistState() {
    try {
      window.sessionStorage.setItem(this.#storageKey, JSON.stringify({
        name: this.#state.name,
        talla: this.#state.talla,
        yarnLabel: this.#state.yarnLabel,
        bagLabel: this.#state.bagLabel,
        feltLabels: this.#state.feltLabels,
        feltColors: this.#state.feltColors,
        extraElements: this.#state.extraElements,
        bundle: this.#bundle,
      }));
    } catch (error) {
      // depolama yoksa akış bozulmaz
    }
  }

  #sanitize(raw) {
    let cleaned = raw.replace(NAME_DISALLOWED, '').replace(/\s+/g, ' ').replace(/^\s+/, '');
    cleaned = cleaned.slice(0, this.#maxChars);
    if (cleaned.length > 0) {
      cleaned = cleaned.charAt(0).toLocaleUpperCase('es') + cleaned.slice(1);
    }
    return cleaned;
  }

  #setName(raw, sourceInput) {
    const cleaned = this.#sanitize(raw);
    if (sourceInput && sourceInput.value !== cleaned) {
      const cursor = sourceInput.selectionStart;
      sourceInput.value = cleaned;
      if (cursor !== null) {
        const position = Math.min(cursor, cleaned.length);
        sourceInput.setSelectionRange(position, position);
      }
    }
    this.#state.name = cleaned;

    const { cardInput } = this.#refs;
    if (cardInput && cardInput !== sourceInput) cardInput.value = cleaned;

    if (cleaned.trim() !== '') {
      this.#toggleError(this.#refs.cardError, false);
    }

    this.#persistState();
    this.#syncHiddenInputs();
    this.#syncAppName();
  }

  // Uygulamanın (Aris Product Options) ürün formu içindeki isim alanı.
  #appNameField() {
    const form = this.#productForm();
    if (!form) return null;
    const field = form.elements.namedItem('Çantaya Yazılacak İsim');
    return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement ? field : null;
  }

  #boundAppField = null;
  #boundAppRadios = new WeakSet();

  // Uygulama kendi alanlarını yeniden çizerse dinleyicileri yeni düğümlere bağlar.
  #bindAppField(signal) {
    if (!signal) return;

    const appField = this.#appNameField();
    if (appField && appField !== this.#boundAppField) {
      appField.addEventListener('input', () => {
        if (this.#syncingAppField) return;
        this.#setName(appField.value, null);
      }, { signal });
      this.#boundAppField = appField;
      if (appField.value && !this.#state.name) {
        this.#setName(appField.value, null);
      } else if (this.#state.name) {
        this.#syncAppName();
      }
    }

    // App swatch'ları → blok yansıması (müşteri app tarafından seçerse)
    const form = this.#productForm();
    if (!form) return;
    const radioKinds = { 'Çanta Rengi': 'bag', 'İplik Rengi': 'yarn' };
    form.querySelectorAll('input[type="radio"]').forEach((radio) => {
      if (this.#boundAppRadios.has(radio)) return;
      const kind = radioKinds[radio.name];
      if (!kind) return;
      this.#boundAppRadios.add(radio);
      radio.addEventListener('change', () => {
        if (this.#syncingAppField || !radio.checked) return;
        const mine = this.querySelector('[data-nd-swatch="' + kind + '"][data-value="' + CSS.escape(radio.value) + '"]');
        if (mine) {
          this.#syncingAppField = true;
          if (kind === 'bag') this.#applyBag(mine, { switchVariant: true });
          else this.#applyYarn(mine);
          this.#syncingAppField = false;
          this.#persistState();
          this.#syncHiddenInputs();
        }
      }, { signal });
    });
  }

  #syncAppName() {
    if (this.#isSecondary) return;
    const appField = this.#appNameField();
    if (!appField || appField.value === this.#state.name) return;
    this.#syncingAppField = true;
    appField.value = this.#state.name;
    appField.dispatchEvent(new Event('input', { bubbles: true }));
    appField.dispatchEvent(new Event('change', { bubbles: true }));
    this.#syncingAppField = false;
  }

  #toggleError(element, show) {
    if (!element) return;
    element.hidden = !show;
  }

  // --- talla (boyut) seçimi ---------------------------------------------

  #selectTalla(value) {
    this.#applyTalla(value);
    this.#syncVariant();
    this.#persistState();
    this.#syncHiddenInputs();
  }

  #applyTalla(value) {
    this.#state.talla = value === 'Pequeña' ? 'Pequeña' : 'Mediana';
    this.querySelectorAll('[data-nd-talla]').forEach((button) => {
      button.setAttribute('aria-pressed', (button.dataset.ndTalla || '') === this.#state.talla ? 'true' : 'false');
    });
    // İsim sınırı boyuta göre değişir: input attribute'u, ipucu metni ve
    // mevcut değer birlikte güncellenir.
    const input = this.#refs.cardInput;
    if (input) {
      input.maxLength = this.#maxChars;
      if (input.value.length > this.#maxChars) this.#setName(input.value, input);
    }
    if (this.#refs.cardHintCount) this.#refs.cardHintCount.textContent = String(this.#maxChars);
    this.#state.name = this.#sanitize(this.#state.name);
  }

  // --- swatch seçimleri -------------------------------------------------

  #selectYarn(button) {
    this.#applyYarn(button);
    this.#syncAppRadio('İplik Rengi', this.#state.yarnLabel);
    this.#persistState();
    this.#syncHiddenInputs();
  }

  #applyYarn(button) {
    if (!button) return;
    this.querySelectorAll('[data-nd-swatch="yarn"]').forEach((b) => {
      b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
    });
    this.#state.yarnLabel = button.dataset.value || '';
    this.#state.yarnHex = button.dataset.hex || '';
    // Çoklu renk paketinde sipariş satırına renk dökümü de yazılır:
    // "Pack Amelia (Rosa Viejo, Crema, Rosa)"
    const components = (button.dataset.components || '').trim();
    this.#state.yarnDisplay =
      components && components !== this.#state.yarnLabel
        ? this.#state.yarnLabel + ' (' + components + ')'
        : this.#state.yarnLabel;
  }

  #selectBag(button) {
    this.#applyBag(button, { switchVariant: true });
    this.#syncAppRadio('Çanta Rengi', this.#state.bagLabel);
    this.#syncGalleryToBag();
    this.#persistState();
    this.#syncHiddenInputs();
  }

  // Seçilen çanta rengine ait galeri karesine geçer. Eşleşme dosya adı
  // üzerinden yapılır: renk etiketi sadeleştirilip slide görsellerinin
  // dosya adında aranır, eşleşen karenin thumbnail'ı tıklanır — yani
  // temanın kendi galeri akışı çalışır, özel API yok.
  // Yeni renklerin (Rosa/Azul/Gris/Beige/Mostaza) ürün fotoğrafı henüz
  // yokken data-gallery-slug boş bırakılır; eşleşme bulunamaz ve galeri
  // olduğu gibi kalır (sessiz, bilinçli davranış — fotoğraflar çekilince
  // blok ayarındaki "Galeri eşleşme kelimesi" doldurulur).
  #syncGalleryToBag() {
    if (this.#isSecondary) return;
    const slug = slugifyTr(this.#state.bagGalleryKey || this.#state.bagLabel);
    if (!slug || slug === this.#gallerySlug) return;
    const gallery = this.#section()?.querySelector('media-gallery');
    if (!gallery) return;
    const slides = Array.from(gallery.querySelectorAll('[ref="slides[]"]'));
    const index = slides.findIndex((slide) => {
      const file = (slide.querySelector('img')?.getAttribute('src') || '').split('/').pop() || '';
      return slugifyTr(file).includes(slug);
    });
    if (index < 0) return;
    this.#gallerySlug = slug;
    const thumb = gallery.querySelectorAll('[ref="thumbnails[]"]')[index];
    if (thumb) {
      thumb.click();
      return;
    }
    try {
      gallery.slideshow?.select(index);
    } catch (error) {
      // galeri bileşeni beklenenden farklıysa sessizce geç
    }
  }

  // --- elementler (Figuras / Flor / Mariposa / Arcoíris) ----------------

  // Ücrete esas kategoriler: kartı seçili olanlar + kartsız açık düğmeler.
  // Sıra, element düğmelerinin DOM sırasıdır (sipariş notu tutarlı kalır).
  #activeElements() {
    const withCards = this.#selectedCardCategories();
    const order = Array.from(this.querySelectorAll('[data-nd-element]')).map((b) => b.dataset.ndElement || '');
    return order.filter((cat) => withCards.includes(cat) || this.#state.extraElements.includes(cat));
  }

  #selectedCardCategories() {
    const cats = [];
    this.#state.feltLabels.forEach((label) => {
      const cat = this.#cardCategory(label);
      if (cat && !cats.includes(cat)) cats.push(cat);
    });
    return cats;
  }

  #cardCategory(label) {
    const card = this.querySelector('[data-nd-swatch="felt"][data-value="' + CSS.escape(label) + '"]');
    return card?.dataset.category || '';
  }

  #categoryHasCards(cat) {
    return !!this.querySelector('[data-nd-swatch="felt"][data-category="' + CSS.escape(cat) + '"]');
  }

  // Hicbir kategori acik degilse ilk kartli kategoriyi (DOM sirasi: Figuras)
  // acar — kartlar tiklama beklemeden gorunur. Kategoriyi acmak ucretsizdir;
  // ucret yalnizca kart secildiginde islenir (#activeElements).
  #openDefaultCategory() {
    if (this.#openCats.size) return;
    const first = Array.from(this.querySelectorAll('[data-nd-element]'))
      .map((button) => button.dataset.ndElement || '')
      .find((cat) => this.#categoryHasCards(cat));
    if (first) this.#openCats.add(first);
  }

  #categoryMax(cat) {
    const button = this.querySelector('[data-nd-element="' + CSS.escape(cat) + '"]');
    const parsed = Number.parseInt(button?.dataset.max || '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 2;
  }

  // Element düğmesi: kartlı kategoride şeridi açar/kapatır (kapatırken o
  // kategorinin seçimlerini temizler — knitandcarry'de elementin işaretini
  // kaldırmak alt seçimi ve ücreti düşürür); kartsız kategoride (Arcoíris)
  // doğrudan ücretli seçimdir.
  #toggleElement(cat) {
    if (!cat) return;
    if (this.#categoryHasCards(cat)) {
      const hasSelection = this.#selectedCardCategories().includes(cat);
      if (hasSelection) {
        this.#state.feltLabels = this.#state.feltLabels.filter((label) => this.#cardCategory(label) !== cat);
        this.#openCats.delete(cat);
      } else if (this.#openCats.has(cat)) {
        this.#openCats.delete(cat);
      } else {
        this.#openCats.add(cat);
      }
    } else {
      const index = this.#state.extraElements.indexOf(cat);
      if (index >= 0) this.#state.extraElements.splice(index, 1);
      else this.#state.extraElements.push(cat);
    }
    this.#afterElementsChange();
  }

  // Kart seçimi: aynı kategoride üst sınıra ulaşıldıysa en eski seçim
  // düşürülür (engellemek yerine değiştirmek daha akıcı).
  #toggleFelt(card) {
    if (!card) return;
    const label = card.dataset.value || '';
    const index = this.#state.feltLabels.indexOf(label);
    if (index >= 0) {
      this.#state.feltLabels.splice(index, 1);
    } else {
      const cat = card.dataset.category || '';
      const siblings = this.#state.feltLabels.filter((l) => this.#cardCategory(l) === cat);
      const max = this.#categoryMax(cat);
      if (siblings.length >= max) {
        const oldest = siblings[0];
        this.#state.feltLabels.splice(this.#state.feltLabels.indexOf(oldest), 1);
      }
      this.#state.feltLabels.push(label);
      if (cat) this.#openCats.add(cat);
    }
    this.#afterElementsChange();
  }

  // Renk noktası: kartın görselini değiştirir ve figür henüz seçili
  // değilse seçer (rengi seçmek figürü istemek anlamına gelir).
  #selectFeltColor(card, dot) {
    if (!card || !dot) return;
    const label = card.dataset.value || '';
    this.#state.feltColors[label] = dot.dataset.colorLabel || '';
    if (!this.#state.feltLabels.includes(label)) {
      this.#toggleFelt(card);
      return;
    }
    this.#afterElementsChange();
  }

  #removeFelt(label) {
    const index = this.#state.feltLabels.indexOf(label);
    if (index < 0) return;
    this.#state.feltLabels.splice(index, 1);
    this.#afterElementsChange();
  }

  #removeExtraElement(cat) {
    const index = this.#state.extraElements.indexOf(cat);
    if (index < 0) return;
    this.#state.extraElements.splice(index, 1);
    this.#afterElementsChange();
  }

  #clearElements() {
    if (!this.#state.feltLabels.length && !this.#state.extraElements.length) return;
    this.#state.feltLabels = [];
    this.#state.extraElements = [];
    this.#openCats.clear();
    this.#openDefaultCategory();
    this.#afterElementsChange();
  }

  #afterElementsChange() {
    this.#renderElements();
    this.#renderFeltState();
    this.#renderFeltSummary();
    this.#syncVariant();
    this.#persistState();
    this.#syncHiddenInputs();
  }

  // Sipariş notunda ve özet şeridinde görünen ad: renk çeşidi olan
  // figürlerde renk parantez içinde eklenir ("Libélula (Amarillo)").
  #feltDisplayLabel(label) {
    const color = this.#state.feltColors[label];
    return color ? label + ' (' + color + ')' : label;
  }

  // properties[Elementos] metni: "Figuras: Mickey Mouse + Fresa · Arcoíris"
  #elementsText() {
    const parts = this.#activeElements().map((cat) => {
      const labels = this.#state.feltLabels
        .filter((label) => this.#cardCategory(label) === cat)
        .map((label) => this.#feltDisplayLabel(label));
      return labels.length ? cat + ': ' + labels.join(' + ') : cat;
    });
    return parts.join(' · ') || ELEMENT_NONE_VALUE;
  }

  // Element düğmelerinin basılı/açık durumu + kart şeridinin görünürlüğü.
  #renderElements() {
    const active = this.#activeElements();
    this.querySelectorAll('[data-nd-element]').forEach((button) => {
      const cat = button.dataset.ndElement || '';
      button.setAttribute('aria-pressed', active.includes(cat) ? 'true' : 'false');
      button.classList.toggle('is-open', this.#openCats.has(cat));
    });
    let visible = 0;
    this.querySelectorAll('[data-nd-swatch="felt"]').forEach((card) => {
      const show = this.#openCats.has(card.dataset.category || '');
      card.toggleAttribute('hidden', !show);
      if (show) visible += 1;
    });
    this.#refs.feltList?.toggleAttribute('hidden', visible === 0);
    // Arcoíris açıklaması yalnızca seçiliyken görünür.
    this.#refs.elementHint?.toggleAttribute('hidden', !active.includes('Arcoíris'));
  }

  #renderFeltState() {
    this.querySelectorAll('[data-nd-swatch="felt"]').forEach((card) => {
      const label = card.dataset.value || '';
      const pressed = this.#state.feltLabels.includes(label);
      const pick = card.querySelector('[data-nd-felt-pick]') || card;
      pick.setAttribute('aria-pressed', pressed ? 'true' : 'false');

      const active = this.#state.feltColors[label];
      card.querySelectorAll('[data-nd-felt-color]').forEach((dot) => {
        const on = (dot.dataset.colorLabel || '') === active;
        dot.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) this.#applyFeltImage(card, dot);
      });
    });
  }

  #applyFeltImage(card, dot) {
    const img = card.querySelector('[data-nd-felt-img]');
    if (!img || !dot.dataset.colorImage) return;
    if (img.getAttribute('src') !== dot.dataset.colorImage) {
      img.setAttribute('src', dot.dataset.colorImage);
      if (dot.dataset.colorAlt) img.setAttribute('alt', dot.dataset.colorAlt);
    }
  }

  // Seçim özeti: seçilen kartlar + kartsız elementler çıkarılabilir çipler
  // olarak listelenir; sağda canlı ek ücret durur.
  #renderFeltSummary() {
    const el = this.#refs.feltSummary;
    if (!el) return;
    el.textContent = '';
    const labels = this.#state.feltLabels;
    const extras = this.#state.extraElements;
    if (!labels.length && !extras.length) return;

    const signal = this.#abort?.signal;
    const title = document.createElement('span');
    title.className = 'nd-felt-summary__label';
    title.textContent = 'Seleccionados:';
    el.append(title);

    const addChip = (text, ariaLabel, onRemove) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'nd-felt-chip';
      chip.setAttribute('aria-label', ariaLabel);
      chip.append(text);
      const remove = document.createElement('span');
      remove.className = 'nd-felt-chip__x';
      remove.setAttribute('aria-hidden', 'true');
      remove.textContent = '✕';
      chip.append(remove);
      chip.addEventListener('click', onRemove, { signal });
      el.append(chip);
    };

    labels.forEach((label) => {
      const display = this.#feltDisplayLabel(label);
      addChip(display, 'Quitar ' + display, () => this.#removeFelt(label));
    });
    extras.forEach((cat) => {
      addChip(cat, 'Quitar ' + cat, () => this.#removeExtraElement(cat));
    });

    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'nd-felt-summary__clear';
    clear.textContent = 'Quitar todo';
    clear.addEventListener('click', () => this.#clearElements(), { signal });
    el.append(clear);

    const count = this.elementCount;
    const base = (this.#findVariant([this.#state.talla, ELEMENT_NONE_VALUE]) || this.#findVariant([ELEMENT_NONE_VALUE]))?.priceCents;
    const current = this.currentVariant()?.priceCents;
    const total = document.createElement('span');
    total.className = 'nd-felt-summary__total';
    total.textContent = base && current && current > base
      ? '+' + this.#formatMoney(current - base)
      : count + (count === 1 ? ' elemento' : ' elementos');
    el.append(total);
  }

  #syncVariant() {
    // İkincil panel formun varyantına ve fiyat gösterimlerine dokunmaz;
    // yalnızca birincil paneli kombine fiyat için dürter.
    if (this.#isSecondary) {
      this.#primary()?.refreshPrice();
      return;
    }
    // Seçim gerçek bir varyanta bağlı: temanın gizli varyant seçicisindeki
    // Talla ve element-adımı radio'ları tıklanır ki buton durumu ve form
    // id'si temanın kendi akışıyla güncellensin. Radio yoksa form id'sine
    // düşülür.
    const values = [this.#state.talla, elementCountValue(this.elementCount)];
    let clicked = false;
    for (const value of values) {
      clicked = this.#clickNativeVariantRadio(value) || clicked;
    }
    if (!clicked) {
      const variant = this.currentVariant();
      if (variant) this.#setFormVariant(variant.id);
    }
    // Sayfadaki fiyat gösterimleri (custom liquid + sticky bar) temanın
    // varyant akışına bağlı olmadığından elle güncellenir.
    this.#refreshPriceDisplays();
  }

  // Ana fiyat alanını boyar: bundle=1'de varyantın hazır formatlı değerleri,
  // bundle=2'de iki panelin kuruş toplamından (− indirim) hesaplanan değerler.
  #refreshPriceDisplays() {
    if (this.#isSecondary) return;
    this.#paintBundleCards();
    const v1 = this.currentVariant();
    if (this.#bundle !== 2) {
      const badge = v1?.discountPct > 0 ? '−' + v1.discountPct + ' %' : '';
      this.#paintPrice(v1?.price, v1?.compareAt, badge);
      this.#renderSummary(v1?.price, this.elementCount);
      return;
    }
    const second = this.#secondary();
    const v2 = second ? second.currentVariant() : null;
    if (!v1?.priceCents || !v2?.priceCents) return; // ikincil henüz upgrade olmadıysa; connect sonrası notify ile boyanır
    const price = v1.priceCents + v2.priceCents - this.#bundleDiscountCents;
    const compare = (v1.compareAtCents || v1.priceCents) + (v2.compareAtCents || v2.priceCents);
    const badge = compare > price ? '2.ª Mochila −30 €' : '';
    this.#paintPrice(this.#formatMoney(price), compare > price ? this.#formatMoney(compare) : null, badge);
    this.#renderSummary(this.#formatMoney(price), this.elementCount + (second?.elementCount || 0));
  }

  // Paket kartlarındaki fiyatlar sunucudan figürsüz taban varyantla basılıyor;
  // element seçilince eskiyip gerçek tahsilattan farklı bir tutar gösteriyordu.
  // Kartları da seçili varyantlardan yeniden boyar.
  #paintBundleCards() {
    const v1 = this.currentVariant();
    if (!v1?.priceCents) return;

    const one = this.querySelector('[data-nd-bundle-price="1"]');
    if (one) one.textContent = this.#formatMoney(v1.priceCents);

    const two = this.querySelector('[data-nd-bundle-price="2"]');
    if (!two) return;
    const second = this.#secondary();
    const v2 = second ? second.currentVariant() : null;
    if (!v2?.priceCents) return;

    const full = v1.priceCents + v2.priceCents;
    const net = full - this.#bundleDiscountCents;
    two.textContent = '';
    const struck = document.createElement('s');
    struck.textContent = this.#formatMoney(full);
    two.append(struck, ' ' + this.#formatMoney(net));
  }

  // Sepete ekle butonunun hemen üstündeki özet. Fiyat blokları sayfanın
  // çok yukarısında kaldığından müşteri element eklerken toplamı göremiyordu.
  #renderSummary(priceText, elementCount) {
    const el = this.#refs.summary;
    if (!el) return;
    if (!priceText) {
      el.textContent = '';
      return;
    }
    const parts = [this.#bundle === 2 ? '2 mochilas' : '1 mochila'];
    if (elementCount > 0) parts.push(elementCount + (elementCount === 1 ? ' elemento' : ' elementos'));
    el.innerHTML = '';
    el.append(parts.join(' + ') + ' · ');
    const total = document.createElement('span');
    total.className = 'nd-summary__total';
    total.textContent = priceText;
    el.append(total, ' · Envío gratis');
  }

  #formatMoney(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'EUR');
  }

  #paintPrice(priceText, compareText, badgeText) {
    if (!priceText) return;
    document.querySelectorAll('.product-new').forEach((el) => {
      el.textContent = priceText;
    });
    document.querySelectorAll('.sticky-add-to-cart__price .price').forEach((el) => {
      el.textContent = priceText;
    });
    document.querySelectorAll('.product-compare').forEach((el) => {
      el.textContent = compareText || '';
      el.style.display = compareText ? '' : 'none';
    });
    document.querySelectorAll('.product-discount-badge').forEach((el) => {
      if (badgeText) {
        el.textContent = badgeText;
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });
  }

  // --- paket (bundle) seçimi ---------------------------------------------

  #bindBundleCards(signal) {
    this.querySelectorAll('[data-nd-bundle]').forEach((button) => {
      button.addEventListener('click', () => {
        this.#setBundle(Number(button.dataset.ndBundle) === 2 ? 2 : 1);
      }, { signal });
    });
  }

  #setBundle(n) {
    const secondEl = this.#section()?.querySelector('name-designer[data-nd-secondary]');
    if (n === 2 && !secondEl) n = 1; // bundle kapalı ya da panel yoksa tekliye zorla
    this.#bundle = n;
    this.querySelectorAll('[data-nd-bundle]').forEach((button) => {
      button.setAttribute('aria-pressed', Number(button.dataset.ndBundle) === n ? 'true' : 'false');
    });
    const panelTitle = this.querySelector('[data-nd-panel-title]');
    if (panelTitle) panelTitle.hidden = n !== 2;
    if (secondEl) secondEl.hidden = n !== 2;
    this.#persistState();
    this.#refreshPriceDisplays();
  }

  #clickNativeVariantRadio(value) {
    if (!value) return false;
    const section = this.#section();
    const picker = section?.querySelector('variant-picker');
    if (!picker) return false;
    const radios = picker.querySelectorAll('input[type="radio"]');
    for (const radio of radios) {
      if (radio.value === value) {
        if (!radio.checked) radio.click();
        return true;
      }
    }
    return false;
  }

  // Uygulamanın (Aris) aynı isimli radio seçeneğini işaretler; app'in
  // zorunlu alan doğrulaması böylece blok seçimleriyle tatmin edilir.
  #syncAppRadio(groupName, value) {
    if (this.#isSecondary) return;
    const form = this.#productForm();
    if (!form || !value) return;
    const radios = form.querySelectorAll('input[type="radio"]');
    for (const radio of radios) {
      if (radio.name === groupName && radio.value === value) {
        if (!radio.checked) {
          this.#syncingAppField = true;
          radio.click();
          this.#syncingAppField = false;
        }
        return;
      }
    }
  }

  #applyBag(button, { switchVariant }) {
    if (!button) return;
    this.querySelectorAll('[data-nd-swatch="bag"]').forEach((b) => {
      b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
    });
    this.#state.bagLabel = button.dataset.value || '';
    this.#state.bagVariantValue = button.dataset.variantValue || button.dataset.value || '';
    this.#state.bagGalleryKey = button.dataset.gallerySlug || '';

    // Çanta rengi planlanan İspanya ürününde varyant DEĞİL (varyantlar
    // Talla × Elementos); ayarda bir varyant değeri yine de girilmişse
    // eski davranış korunur.
    const variant = this.#findVariant([this.#state.bagVariantValue]);
    if (switchVariant && variant) {
      this.#setFormVariant(variant.id);
    }
  }

  // Verilen değerlerin TÜMÜNÜ seçeneklerinde taşıyan varyantı bulur
  // (ör. ["Mediana", "2 elementos"]). Tek değerle de çağrılabilir.
  #findVariant(optionValues) {
    const targets = (Array.isArray(optionValues) ? optionValues : [optionValues])
      .filter(Boolean)
      .map((value) => String(value).toLocaleLowerCase('tr').trim());
    if (!targets.length) return null;
    return (
      this.#data.variants?.find((variant) => {
        const options = (variant.options || []).map((value) => String(value).toLocaleLowerCase('tr').trim());
        return targets.every((target) => options.includes(target));
      }) || null
    );
  }

  #productForm() {
    const formId = this.dataset.formId;
    return formId ? document.getElementById(formId) : null;
  }

  #setFormVariant(variantId) {
    if (this.#isSecondary) return;
    const form = this.#productForm();
    const idInput = form?.querySelector('input[name="id"]');
    if (idInput && variantId) {
      idInput.value = String(variantId);
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // --- Shopify entegrasyonu --------------------------------------------

  #syncHiddenInputs() {
    const { tallaProperty, bagProperty, yarnProperty, elementsProperty, configProperty } = this.#refs;
    const properties = this.lineProperties();
    if (tallaProperty) tallaProperty.value = properties['Talla'];
    if (bagProperty) bagProperty.value = properties['Color de la mochila'];
    if (yarnProperty) yarnProperty.value = properties['Color del hilo'];
    if (elementsProperty) elementsProperty.value = properties['Elementos'];
    if (configProperty) configProperty.value = properties['_config'];
  }

  // Tema, submit olaylarını document üzerinde capture fazında dinlediği
  // için form dinleyicisi yerine product-form-component.handleSubmit
  // örnek metodu sarmalanır (v1 name-preview ile aynı teknik).
  #guardAddToCart() {
    const section = this.#section();
    if (!section) return;
    const component = section.querySelector('product-form-component');
    if (!component || component.dataset.nameDesignerGuarded === 'true') return;
    if (typeof component.handleSubmit !== 'function') return;

    const original = component.handleSubmit.bind(component);
    component.handleSubmit = (event) => {
      const sectionRoot = component.closest('.shopify-section');
      const designer = sectionRoot?.querySelector('name-designer:not([data-nd-secondary])');
      if (!(designer instanceof NameDesigner)) {
        original(event);
        return;
      }
      if (designer.value === '') {
        // Müşteri ismi yalnızca uygulamanın (Aris) alanına yazmış olabilir;
        // o doluysa değeri devral ve eklemeye izin ver.
        const appValue = (designer.appNameValue() || '').trim();
        if (appValue === '') {
          event.preventDefault();
          designer.showError();
          return;
        }
        designer.adoptName(appValue);
      }
      designer.ensureAppSync();

      if (designer.bundle !== 2) {
        original(event); // teklinin yolu bugünküyle birebir
        return;
      }

      // Paket modu: temanın tekli FormData akışı yerine iki item'lı JSON POST.
      event.preventDefault();
      const second = sectionRoot?.querySelector('name-designer[data-nd-secondary]');
      if (!(second instanceof NameDesigner)) {
        original(event); // ikincil panel yoksa tekliye düş
        return;
      }
      if (second.value === '') {
        second.showError();
        return;
      }
      designer.submitBundle(component);
    };
    component.dataset.nameDesignerGuarded = 'true';
    this.#patchedComponent = component;
    this.#originalHandleSubmit = original;
  }

  // Paket ekleme: iki çanta tek /cart/add.js isteğinde (items[] + per-item
  // properties + sections). Başarıda temanın kendi CartAddEvent akışı
  // tetiklenir; çekmece açılışı/morph/sayaç mevcut dinleyicilerle çalışır.
  async submitBundle(component) {
    if (this.#bundleSubmitting) return;
    const second = this.#secondary();
    if (!second) return;

    const v1 = this.currentVariant();
    const v2 = second.currentVariant();
    if (!v1 || !v2) {
      this.#showCartError(component, 'La selección no coincide con ninguna variante; recarga la página, por favor.');
      return;
    }
    if (v1.available === false || v2.available === false) {
      this.#showCartError(component, 'La combinación elegida no está disponible ahora mismo.');
      return;
    }

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const payload = {
      items: [
        { id: v1.id, quantity: 1, properties: this.lineProperties() },
        { id: v2.id, quantity: 1, properties: second.lineProperties() },
      ],
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };

    this.#bundleSubmitting = true;
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      }).then((r) => r.json());

      if (response.status) {
        component?.dispatchEvent(
          new CartErrorEvent(component.id || '', response.message, response.description, response.errors)
        );
        this.#showCartError(component, response.description || response.message || 'No se pudo añadir al carrito.');
        return;
      }

      const cart = await fetch(Theme.routes.cart_url + '.js')
        .then((r) => r.json())
        .catch(() => undefined);
      component?.dispatchEvent(
        new CartAddEvent(cart ?? {}, String(v1.id), {
          source: 'product-form-component',
          itemCount: 2,
          productId: this.dataset.productId,
          sections: response.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#bundleSubmitting = false;
    }
  }

  // Temanın sepete-ekle hata satırını (buy-buttons içindeki span) yeniden
  // kullanır: metin düğümü + canlı bölge + 10 sn sonra otomatik gizleme.
  #showCartError(component, message) {
    const errorEl = component?.refs?.addToCartTextError;
    if (!errorEl) {
      console.error(message);
      return;
    }
    errorEl.classList.remove('hidden');
    const textNode = errorEl.childNodes[2];
    if (textNode) textNode.textContent = message;
    else errorEl.appendChild(document.createTextNode(message));
    if (component.refs.liveRegion) component.refs.liveRegion.textContent = message;
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => {
      errorEl.classList.add('hidden');
      if (component.refs.liveRegion) component.refs.liveRegion.textContent = '';
    }, 10000);
  }
}

if (!customElements.get('name-designer')) {
  customElements.define('name-designer', NameDesigner);
}
